import axios from 'axios';
import { MapLocation, VehicleType, TollDetails, PaymentMethod, TollStop } from '../types';
import { supabase } from '../supabase/client';

// Use environment variable for API key with fallback
const TOLLGURU_API_KEY = import.meta.env.VITE_TOLLGURU_API_KEY || '3Q2Q8MBT6D7GmJM8QL2rhRNbqJTBr44r';
// Configure Supabase Edge Function endpoint for production
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const PROXY_API_URL = `${SUPABASE_URL}/functions/v1/toll-calculator`;
const CACHE_DURATION_HOURS = 24;

// Map our vehicle types to TollGuru vehicle parameters
function mapVehicleToTollGuru(vehicleType: VehicleType): any {
  // Get current time for realistic departure
  const departureTime = Math.floor(Date.now() / 1000); // Current time in UNIX timestamp
  
  // Default configurations based on vehicle type
  switch (vehicleType) {
    case VehicleType.PICKUP_TRUCK:
      return {
        vehicleType: '2AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 2.5, unit: 'ton' },
        height: { value: 2.2, unit: 'm' },
        length: { value: 5.5, unit: 'm' },
        width: { value: 2.0, unit: 'm' },
        axles: 2,
        trailers: 0
      };
    case VehicleType.DUAL_WHEEL_PICKUP:
      return {
        vehicleType: '2AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 3.5, unit: 'ton' },
        height: { value: 2.5, unit: 'm' },
        length: { value: 6.0, unit: 'm' },
        width: { value: 2.2, unit: 'm' },
        axles: 2,
        trailers: 0
      };
    case VehicleType.RABON:
      return {
        vehicleType: '3AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 10, unit: 'ton' },
        height: { value: 3.0, unit: 'm' },
        length: { value: 8.0, unit: 'm' },
        width: { value: 2.5, unit: 'm' },
        axles: 3,
        trailers: 0
      };
    case VehicleType.TORTON:
      return {
        vehicleType: '4AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 15, unit: 'ton' },
        height: { value: 3.5, unit: 'm' },
        length: { value: 9.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 4,
        trailers: 0
      };
    case VehicleType.SINGLE_WITH_2_AXLE_TRAILER:
      return {
        vehicleType: '5AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 25, unit: 'ton' },
        height: { value: 4.0, unit: 'm' },
        length: { value: 16.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 5,
        trailers: 1,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 8.0, 1.3]
        }
      };
    case VehicleType.SINGLE_WITH_3_AXLE_TRAILER:
      return {
        vehicleType: '6AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 35, unit: 'ton' },
        height: { value: 4.0, unit: 'm' },
        length: { value: 18.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 6,
        trailers: 1,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 8.0, 1.3, 1.3]
        }
      };
    case VehicleType.FULL_6_AXLES:
      return {
        vehicleType: '6AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 40, unit: 'ton' },
        height: { value: 4.0, unit: 'm' },
        length: { value: 19.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 6,
        trailers: 2,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 6.0, 1.3, 4.0]
        }
      };
    case VehicleType.FULL_7_AXLES:
      return {
        vehicleType: '7AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 48, unit: 'ton' },
        height: { value: 4.2, unit: 'm' },
        length: { value: 22.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 7,
        trailers: 2,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 6.0, 1.3, 1.3, 4.0]
        }
      };
    case VehicleType.FULL_8_AXLES:
      return {
        vehicleType: '8AxlesTruck', 
        departure_time: departureTime,
        weight: { value: 55, unit: 'ton' },
        height: { value: 4.3, unit: 'm' },
        length: { value: 22.5, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 8,
        trailers: 2,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 6.0, 1.3, 1.3, 1.3, 4.0]
        }
      };
    case VehicleType.FULL_9_AXLES:
      return {
        vehicleType: '9AxlesTruck',
        departure_time: departureTime,
        weight: { value: 60, unit: 'ton' },
        height: { value: 4.3, unit: 'm' },
        length: { value: 23.0, unit: 'm' },
        width: { value: 2.6, unit: 'm' },
        axles: 9,
        trailers: 2,
        axleSpacing: {
          unit: 'm',
          values: [3.5, 1.3, 6.0, 1.3, 1.3, 1.3, 1.3, 4.0]
        }
      };
    default:
      // Default to 2-axle truck if unspecified
      return {
        vehicleType: '2AxlesTruck',
        departure_time: departureTime,
        weight: { value: 3.5, unit: 'ton' },
        axles: 2,
        trailers: 0
      };
  }
}

// Check if there's a valid cached toll data entry
async function checkCache(origin: string, destination: string, vehicleType: VehicleType) {
  try {
    const { data: cacheEntries, error } = await supabase
      .from('toll_cache')
      .select('*')
      .eq('origin', origin)
      .eq('destination', destination)
      .eq('vehicle_type', vehicleType)
      .gt('expires_at', new Date().toISOString())
      .limit(1);
    
    if (error) {
      console.error('Error checking toll cache:', error);
      return null;
    }
    
    if (cacheEntries && cacheEntries.length > 0) {
        console.log('Found valid cache entry for toll data');
      const cacheEntry = cacheEntries[0];
      return {
        ...cacheEntry.toll_details,
        lastUpdated: new Date(cacheEntry.toll_details.lastUpdated),
        tollCurrency: cacheEntry.toll_details.tollCurrency as Currency // Retrieve from cache
          }
    }

    console.log('No cache found for toll data');
    return null;
  } catch (error) {
    console.error('Error checking toll cache:', error);
    return null;
  }
}

// Save toll data to the cache
async function saveToCache(
  origin: string,
  destination: string,
  vehicleType: VehicleType,
  tollDetails: TollDetails
) {
  try {
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + CACHE_DURATION_HOURS);

    // Create simple serializable tollDetails object (pure JSON, no Symbols or functions)
    const serializableTollDetails = {
      tollStops: tollDetails.tollStops.map(stop => ({
        name: stop.name,
        location: {
          lat: stop.location.lat,
          lng: stop.location.lng
        },
        cost: stop.cost,
        distance: stop.distance,
        operatedBy: stop.operatedBy,
        paymentMethods: stop.paymentMethods.slice() // Create a new array with primitive values
      })),
      totalTollCost: tollDetails.totalTollCost,
      lastUpdated: tollDetails.lastUpdated.toISOString(),
      tollCurrency: tollDetails.tollCurrency // Save the new field
    };

    const { error } = await supabase.from('toll_cache').insert({
      origin,
      destination,
      vehicle_type: vehicleType,
      toll_details: serializableTollDetails,
      created_at: new Date().toISOString(),
      expires_at: expiresAt.toISOString()
    });
    
    if (error) throw error;
    
    console.log('Saved toll data to cache');
  } catch (error) {
    console.error('Error saving toll cache:', error);
  }
}

// Mock data for testing - remove in production
const MOCK_TOLL_DATA = {
  tollStops: [
    {
      name: "Caseta Tepotzotlán",
      location: { lat: 19.712, lng: -99.210 },
      cost: 46.0,
      distance: 42.3,
      operatedBy: "CAPUFE",
      paymentMethods: [PaymentMethod.CASH, PaymentMethod.TAG, PaymentMethod.IAVE]
    },
    {
      name: "Caseta Palmillas",
      location: { lat: 20.404, lng: -100.095 },
      cost: 139.0,
      distance: 158.5,
      operatedBy: "CAPUFE",
      paymentMethods: [PaymentMethod.CASH, PaymentMethod.TAG, PaymentMethod.IAVE]
    },
    {
      name: "Caseta Chichimequillas",
      location: { lat: 20.708, lng: -100.452 },
      cost: 63.0,
      distance: 196.9,
      operatedBy: "CAPUFE",
      paymentMethods: [PaymentMethod.CASH, PaymentMethod.TAG, PaymentMethod.IAVE]
    }
  ],
  totalTollCost: 248.0,  // This is the sum of all toll costs: 46 + 139 + 63 = 248
  lastUpdated: new Date()
};

// Function to determine if we should use mock data
function shouldUseMockData(origin: MapLocation, destination: MapLocation): boolean {
  // Mexico City to Queretaro route (roughly)
  const isMexicoCityToQueretaro = (
    (origin.lat > 19.3 && origin.lat < 19.5 && origin.lng > -99.2 && origin.lng < -99.1) && 
    (destination.lat > 20.5 && destination.lat < 20.7 && destination.lng > -100.5 && destination.lng < -100.3)
  );
  
  // Determine if we should use mock data based on environment and route
  return import.meta.env.DEV && isMexicoCityToQueretaro;
}

// Map TollGuru API response to our TollDetails type
function mapTollGuruResponse(response: any): TollDetails {
  console.log('Mapping TollGuru API response to our format');
  
  if (!response?.routes || response.routes.length === 0) {
    console.error('Invalid TollGuru API response format - no routes found');
    throw new Error('Invalid TollGuru API response format');
  }

  const tollStops: TollStop[] = [];
  let totalTollCost = 0;

  // Get the recommended route or the first route
  const route = response.routes.find((r: any) => r.summary?.recommended) || response.routes[0];

  // Extract currency from the route's costs, default to MXN if not found
  const tollCurrency = route.costs?.currency || 'MXN';
  
  // Debug information
  console.log(`Processing route with summary:`, JSON.stringify(route.summary || {}, null, 2));
  
  // Extract toll information
  if (route.tolls && route.tolls.length > 0) {
    console.log(`Found ${route.tolls.length} toll stops in response`);
    
    for (const toll of route.tolls) {
      // Extract necessary data
      const tollCost = parseFloat(toll.tagCost || toll.cashCost || 0);
      const lat = toll.lat || 0;
      const lng = toll.lng || 0;
      
      // Debug toll information
      console.log(`Processing toll stop: ${toll.name || 'Unknown'}, cost: ${tollCost}`);
      
      // Create toll stop object
      const tollStop: TollStop = {
        name: toll.name || 'Caseta sin nombre',
        location: {
          lat: Number(lat),
          lng: Number(lng)
        },
        cost: tollCost,
        distance: Number(toll.distance / 1000 || 0), // Convert to km if needed
        operatedBy: String(toll.operator || toll.road || 'Operador Desconocido'),
        paymentMethods: determineTollPaymentMethods(toll)
      };

      totalTollCost += tollCost;
      tollStops.push(tollStop);
    }
    
    // Recalculate total from individual tolls to ensure consistency
    totalTollCost = tollStops.reduce((sum, stop) => sum + stop.cost, 0);
    console.log(`Recalculated total toll cost: ${totalTollCost}`);
  } else {
    console.log('No toll stops found in the response');
  }

  // If no toll stops were found but we had routes, create an empty result
  if (tollStops.length === 0) {
    console.log('Returning empty toll details (no toll stops found)');
    return {
      tollStops: [],
      totalTollCost: 0,
      lastUpdated: new Date(),
      tollCurrency: tollCurrency as Currency
    };
  }

  console.log(`Successfully mapped ${tollStops.length} toll stops with total cost: ${totalTollCost}`);
  
  return {
    tollStops,
    totalTollCost,
    lastUpdated: new Date(),
    tollCurrency: tollCurrency as Currency
  };
}

// Helper function to determine payment methods based on toll data
function determineTollPaymentMethods(toll: any): PaymentMethod[] {
  const methods: PaymentMethod[] = [];
  
  // Add cash if cashCost exists
  if (toll.cashCost !== undefined) {
    methods.push(PaymentMethod.CASH);
  }
  
  // Add tag/transponder if tagCost exists
  if (toll.tagCost !== undefined) {
    methods.push(PaymentMethod.IAVE);
    methods.push(PaymentMethod.TAG);
  }
  
  // Add card as a default payment method if no specifics are given
  if (methods.length === 0 || (toll.cashCost !== undefined && toll.tagCost !== undefined)) {
    methods.push(PaymentMethod.CARD);
  }
  
  // Ensure we always have at least one payment method
  if (methods.length === 0) {
    methods.push(PaymentMethod.CASH);
  }
  
  return methods;
}

// Helper function to sanitize location objects and ensure they're cloneable
function sanitizeLocation(location: any): MapLocation {
  if (!location) {
    return { lat: 0, lng: 0 };
  }
  
  // Extract numeric values only to avoid Symbol properties
  try {
    const lat = typeof location.lat === 'function' 
      ? Number(location.lat()) 
      : Number(location.lat);
      
    const lng = typeof location.lng === 'function' 
      ? Number(location.lng()) 
      : Number(location.lng);
    
    return { lat, lng };
  } catch (e) {
    console.error('Error sanitizing location object:', e);
    return { lat: 0, lng: 0 };
  }
}

// Main function to get toll costs
export async function getTollCosts(
  origin: MapLocation,
  destination: MapLocation,
  vehicleType: VehicleType,
  originAddress: string,
  destinationAddress: string,
  waypoints: MapLocation[] = []
): Promise<TollDetails> {
  try {
    // Ensure we're working with plain objects without any Symbol properties
    const safeOrigin = sanitizeLocation(origin);
    const safeDestination = sanitizeLocation(destination);
    const safeWaypoints = waypoints.map(wp => sanitizeLocation(wp));

    // Debug logging
    console.log('getTollCosts called with:', {
      origin: `${safeOrigin.lat},${safeOrigin.lng}`,
      destination: `${safeDestination.lat},${safeDestination.lng}`,
      waypoints: safeWaypoints.map(wp => `${wp.lat},${wp.lng}`),
      vehicleType,
      originAddress,
      destinationAddress
    });

    // Option to use mock data for testing or development
    if (shouldUseMockData(safeOrigin, safeDestination)) {
      console.log('Using mock toll data for development');
      return { ...MOCK_TOLL_DATA, lastUpdated: new Date() };
    }

    // Check cache first - only use cache for exact same vehicle type
    const cachedData = await checkCache(originAddress, destinationAddress, vehicleType);
    if (cachedData) {
      console.log('Using cached toll data for vehicle type:', vehicleType);
      // Convert lastUpdated to Date object if needed
      const tollDetails: TollDetails = {
        ...cachedData,
        lastUpdated: new Date(cachedData.lastUpdated)
      };
      return tollDetails;
    }

    // When running in dev mode, check if the Express server is running
    if (import.meta.env.DEV) {
      try {
        // Try to ping the server first
        await axios.get('/api/health-check', { timeout: 2000 });
        console.log('API server is running');
      } catch (pingError) {
        console.warn('Express proxy server not running:', pingError);
        console.log('Falling back to mock data since API server is not available');
        
        // Since the server isn't running in development, use mock data
        const mockTollDetails = { ...MOCK_TOLL_DATA, lastUpdated: new Date() };
        return mockTollDetails;
      }
    }

    // Verify API key is available
    if (!TOLLGURU_API_KEY) {
      console.error('No TollGuru API key found');
      // Instead of throwing, return empty toll details
      return {
        tollStops: [],
        totalTollCost: 0,
        lastUpdated: new Date()
      };
    }

    // Prepare vehicle parameters based on selected vehicle type
    const vehicleConfig = mapVehicleToTollGuru(vehicleType);
    console.log('Requesting toll costs with vehicle config:', JSON.stringify(vehicleConfig, null, 2));
    
    // Prepare request body for TollGuru API
    const requestBody = {
      from: {
        lat: safeOrigin.lat,
        lng: safeOrigin.lng
      },
      to: {
        lat: safeDestination.lat,
        lng: safeDestination.lng
      },
      waypoints: safeWaypoints.map(wp => ({
        lat: wp.lat,
        lng: wp.lng
      })),
      serviceProvider: "here",
      vehicle: vehicleConfig
    };

    // Make API request to Supabase Edge Function
    console.log('Making request to toll calculator proxy for vehicle type:', vehicleType);
    console.log('API URL:', PROXY_API_URL);
    console.log('Request data:', JSON.stringify(requestBody, null, 2));
    
    // Get the current user session for authorization
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('No valid session found for toll calculation');
      throw new Error('Authentication required for toll calculation');
    }
    
    // Create AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 20000); // 20 second timeout
    
    let response;
    try {
      response = await fetch(PROXY_API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      // Clear timeout since request completed
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP Error ${response.status}` };
        }
        
        console.error('API error status:', response.status);
        console.error('API error data:', errorData);
        
        throw new Error(`Request failed with status ${response.status}: ${errorData.error || response.statusText}`);
      }
      
      const responseData = await response.json();
      
      console.log('API response received, status:', response.status);
      
      if (!responseData) {
        console.error('Empty response from toll calculator proxy');
        // Return empty toll details instead of throwing
        return {
          tollStops: [],
          totalTollCost: 0,
          lastUpdated: new Date()
        };
      }
      
      // Log the response status and type for debugging
      console.log('Response data type:', typeof responseData);
      console.log('Response has routes:', !!responseData.routes);
      
      if (responseData.routes && responseData.routes.length > 0) {
        const hasTolls = responseData.routes.some((route: any) => 
          route.tolls && route.tolls.length > 0
        );
        console.log('Routes with tolls found:', hasTolls);
        
        if (!hasTolls) {
          console.log('Routes found but no tolls in the response');
          return {
            tollStops: [],
            totalTollCost: 0,
            lastUpdated: new Date()
          };
        }
      }
      
      // Process response and convert it to our TollDetails format
      const tollDetails = mapTollGuruResponse(responseData);
      console.log('Processed toll details:', 
        JSON.stringify({
          tollStopCount: tollDetails.tollStops.length,
          totalTollCost: tollDetails.totalTollCost,
          vehicleType
        }, null, 2)
      );
      
      if (tollDetails.tollStops.length === 0) {
        console.log('No toll stops found after processing response');
      }
      
      // Cache and return results
      await saveToCache(originAddress, destinationAddress, vehicleType, tollDetails);
      return tollDetails;
      
    } catch (fetchError: any) {
      // Clear timeout in case of error
      clearTimeout(timeoutId);
      
      // Handle specific fetch errors
      if (fetchError.name === 'AbortError') {
        console.error('Request timeout after 20 seconds');
        throw new Error('Request to TollGuru API timed out after 20 seconds');
      }
      
      // Re-throw other errors to be handled by outer catch
      throw fetchError;
    }

  } catch (error: any) {
    console.error('Error calculating toll costs:', error);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    console.error('Error name:', error?.name);
    console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
    
    // Additional debugging for the specific error
    if (error instanceof TypeError) {
      console.error('TypeError details - this might be a property access issue');
    }
    if (error instanceof ReferenceError) {
      console.error('ReferenceError details - this might be an undefined variable');
    }
    
    // If we're in development mode, return mock data instead of empty results
    if (import.meta.env.DEV) {
      console.log('Returning mock data due to error in development mode');
      return { ...MOCK_TOLL_DATA, lastUpdated: new Date() };
    }

    // Ensure tollCurrency is set even on error for consistency
    // Return empty toll details object for production - this allows quotation to continue
    return {
      tollStops: [],
      totalTollCost: 0,
      lastUpdated: new Date()
    };
  }
}